import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import TasksPage from './pages/TasksPage';
import TeamPage from './pages/TeamPage';
import AttendancePage from './pages/AttendancePage';
import TimeTrackingPage from './pages/TimeTrackingPage';
import FreelancersPage from './pages/FreelancersPage';
import FreelancerPortalPage from './pages/FreelancerPortalPage';
import LeadsPage from './pages/LeadsPage';
import SalesReportsPage from './pages/SalesReportsPage';
import DataManagementPage from './pages/DataManagementPage';
import AccountsPage from './pages/AccountsPage';
import CalendarPage from './pages/CalendarPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import MyShootsPage from './pages/MyShootsPage';
import ReportsPage from './pages/ReportsPage';
import AdminPanelPage from './pages/AdminPanelPage';
import TransfersPage from './pages/TransfersPage';
import TransferDownloadPage from './pages/TransferDownloadPage';
import ClientGalleryPage from './pages/ClientGalleryPage';
import FaceSearchPage from './pages/FaceSearchPage';
import FaceEventPage from './pages/FaceEventPage';
import FindMyPhotosPage from './pages/FindMyPhotosPage';
import RemindersPage from './pages/RemindersPage';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles) {
    const userRoles = user.roles || [user.role];
    if (!allowedRoles.some(r => userRoles.includes(r))) {
      return <Navigate to="/" replace />;
    }
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/projects" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'freelancer']}>
            <ProjectsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/projects/:id" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'freelancer']}>
            <ProjectDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'freelancer']}>
            <TasksPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/team" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <TeamPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/attendance" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'sales_person']}>
            <AttendancePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/time-tracking" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'freelancer']}>
            <TimeTrackingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/freelancers" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <FreelancersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/freelancer-portal" 
        element={
          <ProtectedRoute allowedRoles={['freelancer']}>
            <FreelancerPortalPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/leads" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'sales_person']}>
            <LeadsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sales-reports" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'sales_person']}>
            <SalesReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/data-management" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer']}>
            <DataManagementPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accounts" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <AccountsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/calendar" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'social_media']}>
            <CalendarPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/my-shoots" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer']}>
            <MyShootsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <ReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/transfers" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'social_media', 'freelancer']}>
            <TransfersPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/transfer/:token" element={<TransferDownloadPage />} />
      <Route path="/find/:slug" element={<FindMyPhotosPage />} />
      <Route
        path="/face-search"
        element={
          <ProtectedRoute>
            <FaceSearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/face-search/:id"
        element={
          <ProtectedRoute>
            <FaceEventPage />
          </ProtectedRoute>
        }
      />
      <Route path="/gallery/:clientToken" element={<ClientGalleryPage />} />
      <Route 
        path="/reminders" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'social_media', 'freelancer']}>
            <RemindersPage />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#121212',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
